import { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import {
  LayoutDashboard,
  Store,
  DollarSign,
  BarChart3,
  Settings,
  LogOut,
  Menu,
  X,
  Package,
  Tag,
  PanelBottom,
  ShoppingBag,
  Sparkles,
  Search,
  Bell,
  Briefcase,
  ShoppingCart,
  Wallet,
  Building2,
  Star,
  Sparkles as SparklesIcon,
} from 'lucide-react';
import { Button } from './ui/button';

const menuGroups = [
  {
    title: 'Painel Executivo',
    icon: Briefcase,
    items: [
      { path: '/admin/dashboard', icon: LayoutDashboard, label: 'Dashboard' },
      { path: '/admin/ia-destack', icon: Sparkles, label: 'IA DESTACK Vendas', premium: true },
      { path: '/admin/relatorios', icon: BarChart3, label: 'Relatórios' },
    ],
  },
  {
    title: 'Marketplace',
    icon: ShoppingCart,
    items: [
      { path: '/admin/lojas', icon: Store, label: 'Lojas' },
      { path: '/admin/produtos', icon: Package, label: 'Produtos' },
      { path: '/admin/categorias', icon: Tag, label: 'Categorias' },
      { path: '/admin/pedidos', icon: ShoppingBag, label: 'Central de Pedidos' },
      { path: '/admin/avaliacoes', icon: Star, label: 'Avaliações' },
      { path: '/admin/vitrine-dinamica', icon: SparklesIcon, label: 'Vitrine Dinâmica' },
    ],
  },
  {
    title: 'Financeiro',
    icon: Wallet,
    items: [
      { path: '/admin/financeiro', icon: DollarSign, label: 'Financeiro' },
    ],
  },
  {
    title: 'Empresa',
    icon: Building2,
    items: [
      { path: '/admin/configuracoes/rodape', icon: PanelBottom, label: 'Empresa & Rodapé' },
      { path: '/admin/configuracoes', icon: Settings, label: 'Configurações' },
    ],
  },
];

const formatDateTime = (d) => ({
  date: d.toLocaleDateString('pt-BR', { weekday: 'long', day: '2-digit', month: 'short' }),
  time: d.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' }),
});

export const Layout = ({ children }) => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [now, setNow] = useState(new Date());
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();

  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 30000);
    return () => clearInterval(t);
  }, []);

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const dt = formatDateTime(now);

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Sidebar Overlay (mobile) */}
      <div
        className={`fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-30 lg:hidden transition-opacity ${
          sidebarOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'
        }`}
        onClick={() => setSidebarOpen(false)}
      />

      {/* Sidebar */}
      <aside
        className={`fixed inset-y-0 left-0 w-72 bg-gradient-to-b from-slate-900 via-blue-950 to-slate-900 text-white z-40 transform transition-transform duration-300 shadow-2xl ${
          sidebarOpen ? 'translate-x-0' : '-translate-x-full'
        } lg:translate-x-0 flex flex-col`}
        data-testid="admin-sidebar"
      >
        {/* Brand */}
        <div className="px-5 pt-6 pb-4 border-b border-white/10">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-500 to-violet-600 flex items-center justify-center shadow-lg">
                <Sparkles className="w-5 h-5" />
              </div>
              <div className="leading-tight">
                <p className="font-bold text-lg tracking-tight">DESTACK</p>
                <p className="text-[10px] uppercase tracking-widest text-blue-300">Vendas+ Admin</p>
              </div>
            </div>
            <button
              onClick={() => setSidebarOpen(false)}
              className="lg:hidden p-1.5 rounded-full hover:bg-white/10"
              aria-label="Fechar menu"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Nav groups */}
        <nav className="flex-1 overflow-y-auto py-4 px-3 space-y-4">
          {menuGroups.map((group) => {
            const GroupIcon = group.icon;
            return (
              <div key={group.title}>
                <div className="px-3 mb-1.5 flex items-center gap-2 text-[10px] uppercase tracking-widest text-blue-300/70 font-semibold">
                  <GroupIcon className="w-3 h-3" />
                  {group.title}
                </div>
                <ul className="space-y-0.5">
                  {group.items.map((item) => {
                    const Icon = item.icon;
                    const active = location.pathname === item.path ||
                      (item.path !== '/admin/dashboard' && location.pathname.startsWith(item.path));
                    return (
                      <li key={item.path}>
                        <Link
                          to={item.path}
                          onClick={() => setSidebarOpen(false)}
                          className={`group flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm transition-all ${
                            active
                              ? 'bg-gradient-to-r from-blue-600/90 to-violet-600/90 text-white shadow-md shadow-blue-900/50'
                              : 'text-slate-300 hover:bg-white/5 hover:text-white'
                          }`}
                          data-testid={`sidebar-link-${item.label.toLowerCase().replace(/ /g, '-')}`}
                        >
                          <Icon className={`w-4 h-4 transition-transform ${active ? '' : 'group-hover:scale-110'}`} />
                          <span className="flex-1">{item.label}</span>
                          {item.premium && (
                            <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-amber-400/90 text-slate-900 font-bold">
                              IA
                            </span>
                          )}
                        </Link>
                      </li>
                    );
                  })}
                </ul>
              </div>
            );
          })}
        </nav>

        {/* User card */}
        <div className="px-3 pb-4 pt-3 border-t border-white/10">
          <div className="rounded-xl bg-white/5 backdrop-blur p-3 flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center font-bold text-slate-900 shadow-md">
              {(user?.name || 'A').charAt(0).toUpperCase()}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold truncate">{user?.name || 'Admin'}</p>
              <p className="text-[10px] text-blue-300 uppercase tracking-widest">Administrador</p>
            </div>
            <button
              onClick={handleLogout}
              className="p-2 rounded-lg text-slate-400 hover:text-white hover:bg-white/10 transition-colors"
              data-testid="logout-btn"
              aria-label="Sair"
            >
              <LogOut className="w-4 h-4" />
            </button>
          </div>
        </div>
      </aside>

      {/* Main area */}
      <div className="lg:pl-72 min-h-screen flex flex-col">
        {/* Top bar */}
        <header className="sticky top-0 z-20 bg-white/85 backdrop-blur border-b border-slate-200">
          <div className="px-4 lg:px-8 h-16 flex items-center gap-3">
            <button
              onClick={() => setSidebarOpen(true)}
              className="lg:hidden p-2 rounded-lg hover:bg-slate-100 text-slate-600"
              data-testid="open-sidebar-btn"
              aria-label="Abrir menu"
            >
              <Menu className="w-5 h-5" />
            </button>

            {/* Global search */}
            <div className="relative flex-1 max-w-xl">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <input
                type="text"
                placeholder="Buscar em pedidos, lojas, produtos..."
                className="w-full h-10 pl-10 pr-4 rounded-full bg-slate-100 border border-transparent focus:bg-white focus:border-blue-400 focus:outline-none text-sm placeholder:text-slate-400 transition-colors"
                data-testid="global-search"
              />
            </div>

            {/* Right side */}
            <div className="ml-auto flex items-center gap-2">
              <div className="hidden sm:flex flex-col items-end leading-tight pr-2 border-r border-slate-200 mr-1">
                <span className="text-xs font-semibold text-slate-900 capitalize">{dt.date}</span>
                <span className="text-[11px] text-slate-500 font-mono">{dt.time}</span>
              </div>

              <button
                className="relative p-2 rounded-full hover:bg-slate-100 text-slate-600"
                data-testid="notifications-btn"
                aria-label="Notificações"
              >
                <Bell className="w-5 h-5" />
                <span className="absolute top-1.5 right-1.5 w-2 h-2 rounded-full bg-rose-500 ring-2 ring-white" />
              </button>

              <div className="w-9 h-9 rounded-full bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center font-bold text-slate-900 text-sm shadow">
                {(user?.name || 'A').charAt(0).toUpperCase()}
              </div>
            </div>
          </div>
        </header>

        {/* Content */}
        <main className="flex-1 px-4 lg:px-8 py-6 lg:py-8">
          {children}
        </main>
      </div>
    </div>
  );
};

export default Layout;
